import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  TrendingUp,
  Eye,
  MousePointerClick,
  CheckCircle2,
  Coins,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { advertiserApi } from '../../api';

interface AnalyticsData {
  totalAds: number;
  activeAds: number;
  totalSpent: number;
  totalImpressions: number;
  totalCompletions: number;
  averageCompletionRate: number;
  dailyStats: Array<{ date: string; impressions: number; completions: number; spent: number }>;
}

export function AdvertiserAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | 'all'>('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const getMockData = (): AnalyticsData => ({
    totalAds: 5,
    activeAds: 3,
    totalSpent: 2830,
    totalImpressions: 1310,
    totalCompletions: 283,
    averageCompletionRate: 21.6,
    dailyStats: [
      { date: '2024-12-22', impressions: 150, completions: 32, spent: 320 },
      { date: '2024-12-23', impressions: 180, completions: 38, spent: 380 },
      { date: '2024-12-24', impressions: 200, completions: 45, spent: 450 },
      { date: '2024-12-25', impressions: 120, completions: 25, spent: 250 },
      { date: '2024-12-26', impressions: 190, completions: 40, spent: 400 },
      { date: '2024-12-27', impressions: 220, completions: 48, spent: 480 },
      { date: '2024-12-28', impressions: 250, completions: 55, spent: 550 },
    ],
  });

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await advertiserApi.getAnalytics(period);
      
      if (response.success && response.analytics) {
        setAnalytics(response.analytics);
        setError(null);
      } else {
        // Use mock data for now
        setAnalytics(getMockData());
      }
    } catch (err: unknown) {
      console.warn('Analytics fetch failed, using mock data');
      // Use mock data on error
      setAnalytics(getMockData());
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-white/60">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="text-sm text-red-300 underline mt-1"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const maxCompletions = analytics.dailyStats.length > 0 
    ? Math.max(...analytics.dailyStats.map(d => d.completions))
    : 1;

  const avgCostPerCompletion = analytics.totalCompletions > 0 
    ? analytics.totalSpent / analytics.totalCompletions 
    : 0;

  return (
    <div className="space-y-4">
      {/* Period Selector */}
      <div className="flex gap-2">
        {[
          { id: '7d' as const, label: '7 Days' },
          { id: '30d' as const, label: '30 Days' },
          { id: 'all' as const, label: 'All Time' },
        ].map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              period === p.id
                ? 'bg-primary text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 rounded-xl p-4 border border-blue-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-white/60">Impressions</span>
          </div>
          <p className="text-xl font-bold text-white">
            {analytics.totalImpressions.toLocaleString()}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-xl p-4 border border-purple-500/20">
          <div className="flex items-center gap-2 mb-2">
            <MousePointerClick className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-white/60">Active Ads</span>
          </div>
          <p className="text-xl font-bold text-white">
            {analytics.activeAds} / {analytics.totalAds}
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-xl p-4 border border-green-500/20">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-xs text-white/60">Completions</span>
          </div>
          <p className="text-xl font-bold text-white">
            {analytics.totalCompletions.toLocaleString()}
          </p>
          <p className="text-xs text-green-400">
            {analytics.averageCompletionRate.toFixed(1)}% completion rate
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-500/20 to-orange-500/5 rounded-xl p-4 border border-orange-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-white/60">Total Spent</span>
          </div>
          <p className="text-xl font-bold text-white">
            {analytics.totalSpent.toLocaleString()}
          </p>
          <p className="text-xs text-orange-400">
            ~{avgCostPerCompletion.toFixed(1)} per completion
          </p>
        </div>
      </div>

      {/* Simple Bar Chart */}
      <div className="bg-surface rounded-xl p-4 border border-white/10">
        <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          Daily Completions
        </h3>
        
        {analytics.dailyStats.length > 0 ? (
          <div className="flex items-end justify-between h-32 gap-1">
            {analytics.dailyStats.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(day.completions / maxCompletions) * 100}%` }}
                  transition={{ delay: i * 0.1, duration: 0.3 }}
                  className="w-full bg-gradient-to-t from-primary to-primary/50 rounded-t-sm min-h-[4px]"
                />
                <span className="text-[10px] text-white/40">
                  {new Date(day.date).toLocaleDateString('en', { weekday: 'short' }).slice(0, 2)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-white/40 text-sm">
            No data available for this period
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            Performance Summary
          </h3>
        </div>

        <div className="p-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-white/50">Total Ads</p>
            <p className="text-lg font-bold text-white">{analytics.totalAds}</p>
          </div>
          <div>
            <p className="text-xs text-white/50">Active Ads</p>
            <p className="text-lg font-bold text-green-400">{analytics.activeAds}</p>
          </div>
          <div>
            <p className="text-xs text-white/50">Avg Completion Rate</p>
            <p className="text-lg font-bold text-primary">{analytics.averageCompletionRate.toFixed(1)}%</p>
          </div>
          <div>
            <p className="text-xs text-white/50">Avg Cost/Completion</p>
            <p className="text-lg font-bold text-orange-400">{avgCostPerCompletion.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
